import {
  AfterContentInit,
  ChangeDetectionStrategy,
  Component,
  ContentChildren,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  ViewChild,
} from '@angular/core';
import { Subject } from 'rxjs';
import { filter, map, pairwise, startWith, takeUntil } from 'rxjs/operators';

import { InfiniteScrollItemDirective } from '../../directives/infinite-scroll-item/infinite-scroll-item.directive';

/** Infinite scroll component */
@Component({
  selector: 'atx-infinite-scroll',
  template:
    '<ng-content></ng-content><div class="intersection-anchor" #anchor></div>',
  styleUrls: ['./infinite-scroll.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class InfiniteScrollComponent
  implements OnInit, OnDestroy, AfterContentInit
{
  /** IntersectionObserver options */
  @Input()
  public options: IntersectionObserverInit = {};

  /** Percentage of scroll items where the scroll should be trigger */
  @Input()
  public scrollTriggerPercentage = 0.6;

  /** Disable the emission of the scrolled event */
  @Input()
  public disabled = false;

  /** Scroll items */
  @ContentChildren(InfiniteScrollItemDirective, { descendants: true })
  private infiniteScrollItemComponents: QueryList<InfiniteScrollItemDirective>;

  /** Intersection output */
  @Output()
  public scrolled = new EventEmitter();

  /**
   * Intersection anchor element
   * Trigger scroll when reach then end of the content
   */
  @ViewChild('anchor', { static: true })
  private anchor: ElementRef<HTMLElement>;

  /** Destroy to unsubscribe observables */
  private destroy$ = new Subject<void>();

  /** Subject to watch the HTMLElement to trigger the scroll */
  private sentinel$: Subject<HTMLElement> = new Subject<HTMLElement>();

  /** IntersectionObserver instance */
  private observer: IntersectionObserver;

  /** Dependencies */
  constructor(private host: ElementRef) {}

  /** Angular's OnInit life cycle hook */
  public ngOnInit() {
    const options = {
      root: this.isHostScrollable() ? this.host.nativeElement : null,
      threshold: 0.01,
      ...this.options,
    };

    this.observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !this.disabled) {
        this.scrolled.emit();
      }
    }, options);

    this.observer.observe(this.anchor.nativeElement);

    this.sentinel$
      .pipe(startWith(null), pairwise(), takeUntil(this.destroy$))
      .subscribe(([previousSentinel, newSentinel]) => {
        if (previousSentinel) {
          this.observer.unobserve(previousSentinel);
        }
        if (newSentinel) {
          this.observer.observe(newSentinel);
        }
      });
  }

  /** Method to detect if scroll is possible on element */
  private isHostScrollable() {
    const style = window.getComputedStyle(this.host.nativeElement);

    return (
      style.getPropertyValue('overflow') === 'auto' ||
      style.getPropertyValue('overflow-y') === 'scroll'
    );
  }

  /** Angular's OnDestroy life cycle hook */
  public ngOnDestroy() {
    this.observer.disconnect();
    this.destroy$.next();
    this.destroy$.complete();
  }

  public ngAfterContentInit(): void {
    this.infiniteScrollItemComponents.changes
      .pipe(
        filter((queryList) => queryList.length > 0),
        map((queryList) => {
          const index = Math.trunc(
            queryList.length * this.scrollTriggerPercentage,
          );
          return this.scrollTriggerPercentage === 1
            ? this.anchor.nativeElement
            : queryList._results[index].host.nativeElement;
        }),
        takeUntil(this.destroy$),
      )
      .subscribe(this.sentinel$);
  }
}
